import {
	faCaretRight as fasCaretRight,
	faMinus as fasMinus,
	faPlus as fasPlus,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import React from "react";
import type { ReactNode } from "react";
import { Accordion } from "react-bootstrap";
import { ClickableBlock } from "./ClickableBlock";

interface Props {
	children: ReactNode;
	title: ReactNode;
	name: string;
	className?: string;
	active?: boolean;
	defaultActive?: boolean;
	disabled?: boolean;
	onClick?: (name: string) => void;
	variant?: "center" | "fullWidth" | "custom" | "custom_just_open";
}

interface State {
	active: boolean;
}

export class CollapseBlock extends React.Component<Props, State> {
	private lastUpdatedDefaultValue: boolean = null;

	public constructor(props: Props) {
		super(props);
		const { defaultActive = false } = props;
		this.state = {
			active: defaultActive,
		};
		this.lastUpdatedDefaultValue = defaultActive;
	}

	public componentDidUpdate(): void {
		const { defaultActive } = this.props;

		if (defaultActive !== undefined && this.lastUpdatedDefaultValue !== defaultActive) {
			this.lastUpdatedDefaultValue = defaultActive;

			this.setState({
				active: defaultActive,
			});
		}
	}

	public render(): ReactNode {
		const { name, children, variant = "fullWidth" } = this.props;
		const active = this.getActive();
		const collapseClassName = variant === "fullWidth" ? "mt-2" : undefined;

		return (
			<Accordion activeKey={active ? name : ""}>
				{this.renderHeader()}
				<Accordion.Collapse className={collapseClassName} eventKey={name}>
					<>{children}</>
				</Accordion.Collapse>
			</Accordion>
		);
	}

	private renderHeader(): ReactNode {
		const { variant = "fullWidth" } = this.props;
		const active = this.getActive();

		switch (variant) {
			case "custom":
				return this.renderCustomVariant();
			case "custom_just_open":
				if (active) {
					return null;
				}

				return this.renderCustomVariant();
			case "center":
				return this.renderCenterVariant();
			default:
				return this.renderFullWidthVariant();
		}
	}

	private renderCenterVariant(): ReactNode {
		const { title, name, className = "border-top text-center py-1 mb-3" } = this.props;
		const active = this.getActive();

		const faIcon = <FontAwesomeIcon icon={fasCaretRight} rotation={active ? 270 : 90} />;

		return (
			<div className={className}>
				<Accordion.Toggle
					className="text-reset cursor-pointer"
					as="div"
					eventKey={name}
					onClick={this.handleToggleClick}
				>
					{faIcon} {title} {faIcon}
				</Accordion.Toggle>
			</div>
		);
	}

	private renderFullWidthVariant(): ReactNode {
		const { title, name } = this.props;
		const active = this.getActive();

		return (
			<Accordion.Toggle
				className="d-flex p-1 mb-2 border-bottom cursor-pointer"
				as="div"
				eventKey={name}
				onClick={this.handleToggleClick}
			>
				{title}
				<FontAwesomeIcon icon={active ? fasMinus : fasPlus} className="ml-auto text-muted" />
			</Accordion.Toggle>
		);
	}

	private renderCustomVariant(): ReactNode {
		const { title } = this.props;
		const { active } = this.state;

		return (
			<ClickableBlock className={active ? "collaps_open" : "collaps_close"} onClick={this.handleToggleClick}>
				{title}
			</ClickableBlock>
		);
	}

	private getActive(): boolean {
		const { active: stateActive } = this.state;
		const { active = stateActive } = this.props;

		return active;
	}

	@autobind
	private handleToggleClick() {
		const { name, onClick, disabled = false } = this.props;
		const { active } = this.state;

		if (disabled) return;

		if (onClick !== undefined) {
			onClick(name);
		} else {
			this.setState({
				active: !active,
			});
		}
	}
}
